<template>
  <div class="space-y-8">
    <div v-if="error" class="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
      {{ errorMessage }}
    </div>

    <IndustryRoleFields :formData="formData" fetch-role-questions />

    <div v-if="formData.questions && formData.questions.length" class="space-y-4 rounded-md border border-slate-200 bg-white p-6">
      <h3 class="text-base font-semibold text-slate-800">Role Specific Questions</h3>
      <div
        v-for="(question, index) in formData.questions"
        :key="question.id"
        class="space-y-2"
      >
        <label :for="`question-${question.id}`" class="field-label">
          {{ question.question }}
        </label>
        <textarea
          :id="`question-${question.id}`"
          v-model="formData.answers[index]"
          rows="4"
          class="field-input resize-none"
          placeholder="Share a concise and relevant answer..."
        ></textarea>
      </div>
    </div>

    <div>
      <label class="field-label" for="grid-availability">Availability</label>
      <select
        id="grid-availability"
        @change="availabilityChange($event)"
        v-model="formData.selectedAvailability"
        class="field-input"
      >
        <option disabled value="">Select an option</option>
        <option v-for="availability in availabilityOptions" :key="availability.value" :value="availability.value">
          {{ availability.label }}
        </option>
      </select>
      <Popover class="relative">
        <PopoverPanel
          v-if="calendarOpen"
          static
          class="absolute z-20 mt-2 w-fit rounded-md border border-slate-200 bg-white p-4 drop-shadow-lg"
        >
          <Datepicker
            v-model="date"
            :min-date="today"
            inline
            autoApply
            @update:modelValue="updateCalendarDate"
            hideOffsetDates
            :enableTimePicker="false"
          />
        </PopoverPanel>
      </Popover>
    </div>

    <div class="rounded-md border border-slate-200 bg-white p-4 space-y-4">
      <div class="flex items-center justify-between">
        <label class="field-label mb-0">Target Remuneration</label>
        <span class="text-xs uppercase tracking-wide text-slate-400">Optional</span>
      </div>

      <div class="space-y-4">
        <div>
          <label class="field-label" for="grid-salary">Salary (Annual)</label>
          <input
            id="grid-salary"
            name="field_salary"
            class="field-input"
            type="number"
            min="0"
            step="0.01"
            v-model="formData.field_salary"
          >
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <div>
            <label class="field-label" for="grid-rate">Rate</label>
            <input
              id="grid-rate"
              name="field_rate"
              class="field-input"
              type="number"
              min="0"
              step="0.01"
              v-model="formData.field_rate"
            >
          </div>

          <div>
            <label class="field-label" for="grid-rateType">Rate type</label>
            <select
              id="grid-rateType"
              class="field-input"
              v-model="formData.field_rateType"
            >
              <option disabled value="">Select an option</option>
              <option value="hour">Hourly</option>
              <option value="day">Daily</option>
              <option value="month">Monthly</option>
            </select>
          </div>
        </div>

        <div v-if="showContractEngagementType" class="pt-2 border-t border-slate-200">
          <div class="flex items-center justify-between">
            <label class="field-label mb-0" for="grid-contractType">Contract Engagement Type</label>
            <span class="text-xs uppercase tracking-wide text-slate-400">PLEASE SELECT AN OPTION</span>
          </div>
          <div class="mt-3 grid gap-3 md:grid-cols-2">
            <label
              v-for="option in contractStatusOptions"
              :key="option.value"
              class="contract-option"
              :class="{ 'contract-option--active': formData.field_contractStatus === option.value }"
            >
              <input
                type="radio"
                class="sr-only"
                name="contract-status"
                :value="option.value"
                v-model="formData.field_contractStatus"
              >
              <span class="contract-option__label">{{ option.label }}</span>
              <span class="contract-option__indicator" :class="{ 'contract-option__indicator--active': formData.field_contractStatus === option.value }"></span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div>
      <label class="field-label" for="grid-roleDescription">
        Short description of preferred role/project
      </label>
      <textarea
        id="grid-roleDescription"
        class="field-input resize-none"
        rows="4"
        v-model="formData.field_roleDescription"
        placeholder="Summarise the work you are ideally looking for..."
      ></textarea>
    </div>
  </div>
</template>

<script>
import IndustryRoleFields from './IndustryRoleFields.vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import { Popover, PopoverPanel } from '@headlessui/vue';

export default {
  components: {
    IndustryRoleFields,
    Datepicker,
    Popover,
    PopoverPanel
  },
  props: ['formData'],
  inject: ['toggleLoading'],
  data() {
    return {
      date: new Date(),
      today: new Date(),
      availability: 0,
      calendarOpen: false,
      availabilityOptions: [
        // { "value": 0, "label": "Select availability" },
        { "value": 0.1, "label": "Now"},
        { "value": 0.2, "label": "1 week" },
        { "value": 0.5, "label": "2 weeks" },
        { "value": 1, "label": "1 month" },
        { "value": 999, "label": "Custom date" }
      ],
      error: false,
      errorMessage: "",
      fadeTimeout: null, // To store the timeout for error message fade out
      contractStatusOptions: [
        { value: 'PTY', label: 'PTY', caption: 'Operate as Pty Ltd' },
        { value: 'PAYG', label: 'PAYG', caption: 'Screened payroll' }
      ],
    }
  },
  computed: {
    showContractEngagementType() {
      return this.formData.field_rate !== undefined && this.formData.field_rate !== null && this.formData.field_rate !== '';
    }
  },
  watch: {
    'formData.field_rate'(newVal) {
      if (newVal === '' || newVal === null || newVal === undefined) {
        this.formData.field_contractStatus = '';
        this.formData.field_rateType = '';
      }
    }
  },
  methods: {
    initiateDefaultValues() {
      if (this.formData.selectedAvailability !== undefined) {
        // If selected availability is higher than 1 it means it is a custom date so it needs to be pushed into the array
        if (this.formData.selectedAvailability > 1) {
          const dateObj = new Date(this.formData.selectedAvailability * 1000);

          // Add the new date to the dropdown
          this.availabilityOptions.push({
            "value": this.formData.selectedAvailability,
            "label": "From " + dateObj.toLocaleDateString()
          })
        }
      }

      this.formData.selectedAvailability = this.formData.selectedAvailability !== undefined ? this.formData.selectedAvailability : '';
      if (this.formData.field_contractStatus === undefined) {
        this.formData.field_contractStatus = '';
      }
      if (this.formData.field_salary === undefined) {
        this.formData.field_salary = '';
      }
      if (this.formData.field_rate === undefined) {
        this.formData.field_rate = '';
      }
      this.formData.selectedIndustry = this.formData.selectedIndustry !== undefined ? this.formData.selectedIndustry : '';
      if (this.formData.field_rateType === undefined) {
        this.formData.field_rateType = '';
      }
      this.formData.questions = this.formData.questions !== undefined ? this.formData.questions : [];
      this.formData.answers = this.formData.answers !== undefined ? this.formData.answers : [];
    },
    availabilityChange(event) {
      if (event.target.value == 999) {
        this.calendarOpen = true
      } else {
        this.calendarOpen = false
      }
    },
    updateCalendarDate(newDate) {
      let newSelectedDate = (newDate.getTime() / 1000)
      this.calendarOpen = false

      // If we already have 5 dates, remove the latest one
      if (this.availabilityOptions.length >= 6) {
        this.availabilityOptions.pop();
      }

      // Add the new date to the dropdown
      this.availabilityOptions.push({
        "value": newSelectedDate,
        "label": "From " + newDate.toLocaleDateString()
      })

      this.formData.selectedAvailability = newSelectedDate
    },
    showError(message) {
      this.error = true;
      this.errorMessage = message;

      // Clear previous timeout if it exists
      if (this.fadeTimeout) {
        clearTimeout(this.fadeTimeout);
      }

      // Hide the error after 5 seconds
      this.fadeTimeout = setTimeout(() => {
        this.error = false;
      }, 5000); // Error message stays visible for 5 seconds
    },
    hasValue(value) {
      return value !== undefined && value !== null && String(value).trim() !== '';
    },
    validate() {
      this.toggleLoading(true);
      // Reset error state
      this.error = false;
      this.errorMessage = '';

      let isValid = true;

      // Validation for dynamic answers array
      this.formData.answers.forEach((answer, index) => {
        if (!answer || answer.trim() === '') {
          // this.showError(`All questions are mandatory.`);
          this.errorMessage = 'All questions are mandatory.';
          isValid = false;
        }
      });

      if (!this.formData.selectedRole) {
        // this.showError('Please select a role from the list.');
        this.errorMessage = 'Please select a role from the list.';
        isValid = false;
      }

      if (!this.formData.selectedIndustry) {
        // this.showError('Please select an industry from the list.');
        this.errorMessage = 'Please select an industry from the list.'
        isValid = false;
      }

      if (!this.formData.field_roleDescription) {
        // this.showError('Please provide a short description of your preferred role.');
        this.errorMessage = 'Please provide a short description of your preferred role.'
        isValid = false;
      }

      // if (!this.formData.field_workTravel) {
      //     this.showError('Let us know if you are willing to travel for work.');
      //     this.errorMessage = 'Let us know if you are willing to travel for work.';
      //     isValid = false;
      // }

      // if (!this.formData.field_contractStatus) {
      //   // this.showError('The Contract Status field is mandatory.');
      //   this.errorMessage = 'The Contract Status field is mandatory.';
      //   isValid = false;
      // }

      const salaryValue = this.hasValue(this.formData.field_salary) ? parseFloat(this.formData.field_salary) : null;
      const rateValue = this.hasValue(this.formData.field_rate) ? parseFloat(this.formData.field_rate) : null;
      const hasSalary = salaryValue !== null && !isNaN(salaryValue) && salaryValue > 0;
      const hasRate = rateValue !== null && !isNaN(rateValue) && rateValue > 0;

      if (this.hasValue(this.formData.field_salary) && !hasSalary) {
        this.errorMessage = 'Enter a valid salary amount.';
        isValid = false;
      }

      if (this.hasValue(this.formData.field_rate) && !hasRate) {
        this.errorMessage = 'Enter a valid rate amount.';
        isValid = false;
      }

      if (hasRate) {
        if (!['hour', 'day', 'month'].includes(this.formData.field_rateType)) {
          this.errorMessage = 'Please select how your rate is billed.';
          isValid = false;
        }
        if (!this.formData.field_contractStatus) {
          this.errorMessage = 'Please select your contract engagement type.';
          isValid = false;
        }
      } else {
        this.formData.field_rateType = '';
        this.formData.field_contractStatus = '';
      }

      if (!this.formData.selectedAvailability) {
        // this.showError('Please select your availability.');
        this.errorMessage = 'Please select your availability.';
        isValid = false;
      }

      // If valid, emit the formData
      if (isValid) {
        this.$emit('submit-step3', this.formData); // Emit form data to parent
      } else {
        this.$root.notify(this.errorMessage, 'error');
      }

      this.toggleLoading(false);

      return isValid;
    }
  },
  mounted() {
    this.initiateDefaultValues();
  }
};
</script>

<style scoped>
.form-grid {
  @apply grid gap-6 md:grid-cols-2;
}

.field-label {
  @apply text-sm font-medium text-slate-700;
}

.field-input {
  @apply mt-2 w-full appearance-none rounded-md border border-gray-300 px-4 py-3 text-sm text-gray-700 transition focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300;
}

.field-input--disabled {
  @apply bg-gray-50 text-gray-500 cursor-default;
}

.field-hint {
  @apply mt-1 text-xs text-gray-500;
}

.selection-card {
  @apply flex cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition hover:border-gray-400 hover:bg-gray-50 hover:shadow-sm;
}

.contract-pill {
  @apply flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-gray-400 hover:bg-gray-50 hover:shadow-sm;
}

.contract-option {
  @apply flex items-center justify-between gap-3 rounded-full border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition;
}

.contract-option--active {
  @apply border-brand text-brand shadow-sm;
}

.contract-option__label {
  @apply mx-2;
}

.contract-option__indicator {
  @apply inline-block h-4 w-4 rounded-full border-2 border-gray-300 bg-white transition;
}

.contract-option__indicator--active {
  @apply border-brand bg-brand;
}
</style>
